import { useRef, useState, useEffect } from "react";

import { editOrganization, EditOrganizationType } from "@/api/organization";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { MoonLoader } from "react-spinners";

import { useOrganization } from "@/hooks/server-state/use-organization";

import SaveButton from "@/pages/profile/edit-profile/_components/save-button";

import RichEditor from "@/components/widget/rich-editor";

import OrganizationBanner from "./_components/organization-header";
import StaffManagerList from "./_components/staff-manager-list";
import { StaffManagerType } from "./_components/staff-manager-modal";
import { formatHtml } from "@/lib/utils";

const Loading = () => {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-white">
      <MoonLoader color="#0099A5" size={50} />
    </div>
  );
};

const EditOrganizationPage = () => {
  const { unitName } = useParams<{ unitName: string }>();
  const editorRef1 = useRef<HTMLDivElement>(null); // برای درباره دانشکده
  const editorRef2 = useRef<HTMLDivElement>(null); // برای About Faculty
  const [htmlDraft, setHtmlDraft] = useState<string>("");

  // Fetch organization data - now using unitName from URL parameters or default to "computer"
  const {
    data: organizationData,
    isLoading: isOrganizationLoading,
    error,
    isError
  } = useOrganization(unitName || "", 1);

  const [editData, setEditData] = useState<EditOrganizationType>({
    title: "",
    titleFa: "",
    unitIdentifier: "",
    image: "",
    iconImage: "",
    descriptionEn: "",
    descriptionFa: "",
    bannerDescriptionEn: "",
    bannerDescriptionFa: "",
    website: "",
    backgroundColor: "",
    establishmentYear: 0,
    facultyId: null
  });

  // State for managing staff managers
  const [staffManagers, setStaffManagers] = useState<StaffManagerType[]>([]);

  // Banner image: null means deleted, undefined means unchanged
  const [bannerImage, setBannerImage] = useState<string | null | undefined>(
    undefined
  );

  const isLoading = isOrganizationLoading;

  // Update editData when organizationData is loaded
  useEffect(() => {
    if (organizationData) {
      setEditData({
        title: organizationData.title || "",
        titleFa: organizationData.faculityTitle || "",
        unitIdentifier: "",
        image: organizationData.imageUrl || "",
        iconImage: organizationData.iconImageUrl || "",
        descriptionEn: organizationData.description || "",
        descriptionFa: organizationData.description || "",
        bannerDescriptionEn: organizationData.bannerDescription || "",
        bannerDescriptionFa: organizationData.bannerDescription || "",
        website: organizationData.website || "",
        backgroundColor: organizationData.backgroundColor || "",
        establishmentYear: parseInt(organizationData.establishmentYear) || 0,
        facultyId: null
      });

      // Load organization roles into staffManagers
      if (
        organizationData.organizationRoles &&
        organizationData.organizationRoles.length > 0
      ) {
        const roles = organizationData.organizationRoles.map((role) => ({
          id: `${role.roleId}-${role.professorId ?? 0}-${Date.now()}`,
          roleId: role.roleId,
          roleName: role.roleName,
          userId: role.professorId ?? 0,
          userName: role.professorFullName ?? "",
          userImageUrl: role.professorImageUrl ?? undefined
        }));
        setStaffManagers(roles);
      }

      if (editorRef1.current) {
        editorRef1.current.innerHTML = organizationData.description || "";
        setHtmlDraft(formatHtml(organizationData.description || ""));
      }
      if (editorRef2.current) {
        editorRef2.current.innerHTML = organizationData.bannerDescription || "";
        setHtmlDraft(formatHtml(organizationData.bannerDescription || ""));
      }
    }
  }, [organizationData]);

  const queryClient = useQueryClient();

  const mutateData = useMutation({
    mutationFn: (data: EditOrganizationType) =>
      editOrganization(unitName || "", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization"] });
    }
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const roleToUpdates = staffManagers.map((sm) => ({
      roleId: sm.roleId,
      userId: sm.userId
    }));

    const updatedData = {
      ...editData,
      unitId: organizationData?.id || "",
      descriptionFa: editorRef1.current?.innerHTML || "",
      descriptionEn: editorRef2.current?.innerHTML || "",
      bannerDescriptionFa: "",
      bannerDescriptionEn: "",
      roleToUpdates: roleToUpdates,
      // Only include image if explicitly changed (null = deleted, string = new url)
      ...(bannerImage !== undefined ? { image: bannerImage ?? "" } : {})
    };

    mutateData.mutate(updatedData);
  };

  // Handle error state
  if (isError || error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-red-500">خطا در بارگذاری اطلاعات سازمان</p>
          <p className="mt-2 text-sm text-gray-500">لطفاً دوباره تلاش کنید</p>
          <p className="mt-1 text-xs text-gray-400">
            Error: {error?.message || "Unknown error"}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) return <Loading />;

  return (
    <form onSubmit={handleSubmit}>
      <section>
        <OrganizationBanner
          imageUrl={
            bannerImage !== undefined
              ? (bannerImage ?? undefined)
              : organizationData?.imageUrl
          }
          unitId={organizationData?.id}
          onImageChange={setBannerImage}
        />

        {/* About Faculty Section */}
        <div className="mt-10 px-4">
          <div className="mb-10">
            <RichEditor
              label="درباره دانشکده"
              dir="rtl"
              editorRef={editorRef1}
              editorHeight="200px"
              value={organizationData?.description || ""}
              htmlDraft={htmlDraft}
            />
          </div>

          <div className="mb-10">
            <RichEditor
              label="About Faculty"
              dir="ltr"
              editorRef={editorRef2}
              editorHeight="200px"
              value={organizationData?.bannerDescription || ""}
              htmlDraft={htmlDraft}
            />
          </div>
        </div>

        {/* Faculty Information Section */}
        <div className="mt-10 px-4">
          <h3 className="mb-6 text-lg font-semibold text-gray-800">
            اطلاعات دانشکده
          </h3>
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Website */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  وب‌سایت دانشکده
                </label>
                <div className="relative">
                  <input
                    type="url"
                    value={editData.website}
                    onChange={(e) =>
                      setEditData((prev) => ({
                        ...prev,
                        website: e.target.value
                      }))
                    }
                    placeholder="https://example.com"
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Establishment Year */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  سال تأسیس
                </label>
                <input
                  type="number"
                  value={editData.establishmentYear}
                  onChange={(e) =>
                    setEditData((prev) => ({
                      ...prev,
                      establishmentYear: parseInt(e.target.value) || 0
                    }))
                  }
                  placeholder="مثال: 1400"
                  min="1300"
                  max="1500"
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        <StaffManagerList
          staffManagers={staffManagers}
          onStaffManagersChange={setStaffManagers}
        />

        <SaveButton />

        <div className="pb-20"></div>
      </section>
    </form>
  );
};
export default EditOrganizationPage;
